package unlock

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import debug.BUILD
import kotlin.math.max
import kotlin.math.roundToInt
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event
import puzzlekey.Bundle
import puzzlekey.Envelope
import puzzlekey.GiftBroken
import puzzlekey.GiftOpened
import puzzlekey.HuntStep
import puzzlekey.Projector
import puzzlekey.QrSecret
import puzzlekey.Unlock
import radar.GATHER_SECONDS
import radar.Sparkles
import scan.CameraStage
import scan.Download
import scan.SCAN_INTERVAL_MS
import scan.Scanner
import scan.cameraDenied
import scan.cameraEnded
import scan.describeCameraError
import scan.loadCentre
import scan.loadScanModel
import scan.loadSealed

/** Where her unlock is. The hunt is where it starts; the others are reached from it. */
private sealed interface Screen {
    /** `sealed.json` and a gift kept from before (T16) are still loading. */
    data object Waking : Screen

    data object Hunt : Screen

    /** The camera didn't start: [denied] when she (or Safari's settings) said no. */
    class CameraProblem(val denied: Boolean, val detail: String) : Screen

    data object Recovery : Screen

    class Opened(val bundle: Bundle) : Screen

    /** The Data key opened but the Bundle didn't: nothing she can do. */
    data object Broken : Screen

    /** `sealed.json` or the model didn't load. */
    class NotReady(val detail: String) : Screen
}

/**
 * Her unlock (T15): the QR secret from the URL's `#fragment` (never sent to the server), then the hunt
 * with the Radar's sparkles until a Scan opens the gift, or the Recovery code behind "Stuck?". The look
 * follows docs/design.md. It never names the Puzzle: a Letter thief sees this page too.
 */
@Composable
fun UnlockFlow() {
    val qrSecret = remember { QrSecret.parse(window.location.hash.removePrefix("#")) }
    val errors = remember { mutableStateListOf<String>() }

    DisposableEffect(Unit) {
        val onError: (Event) -> Unit = { e -> errors += "${e.asDynamic().message ?: e.type}" }
        val onRejection: (Event) -> Unit = { e -> errors += "${e.asDynamic().reason}" }
        window.addEventListener("error", onError)
        window.addEventListener("unhandledrejection", onRejection)
        onDispose {
            window.removeEventListener("error", onError)
            window.removeEventListener("unhandledrejection", onRejection)
        }
    }

    Div({ classes("page") }) {
        if (qrSecret == null) NoLetter() else Unlocking(qrSecret, onError = { errors += it })
        Div({ classes("footer") }) {
            if (errors.isNotEmpty()) Pre({ classes("errors") }) { Text(errors.joinToString("\n") { "⚠ $it" }) }
            Div({ classes("build") }) { Text(BUILD) }
        }
    }
}

@Composable
private fun Unlocking(qrSecret: ByteArray, onError: (String) -> Unit) {
    var screen by remember { mutableStateOf<Screen>(Screen.Waking) }
    var unlock by remember { mutableStateOf<Unlock?>(null) }
    var scanner by remember { mutableStateOf<Scanner?>(null) }
    var download by remember { mutableStateOf<Download?>(null) }
    var modelFailed by remember { mutableStateOf(false) }

    // sealed.json first (small), so the Recovery code works while the model is still coming, or if it never does.
    LaunchedEffect(Unit) {
        val sealed = try {
            loadSealed() ?: error("sealed.json isn't a v${Envelope.VERSION} sealed Bundle")
        } catch (e: Throwable) {
            screen = Screen.NotReady("${e.message ?: e}")
            return@LaunchedEffect
        }
        val u = Unlock(sealed, qrSecret)
        unlock = u
        // A gift opened before iOS dropped the tab opens again (T16), without the hunt or the model.
        val kept = try {
            withTimeoutOrNull(KEPT_TIMEOUT_MS) { KeptGift.get() }?.let(u::reopen)
        } catch (e: Throwable) {
            onError("kept gift: ${e.message ?: e}")
            null
        }
        if (kept != null) {
            screen = Screen.Opened(kept)
            return@LaunchedEffect
        }
        screen = Screen.Hunt
        try {
            val shipped = loadCentre().model
            check(shipped == sealed.scan.model) { "sealed.json was enrolled with ${sealed.scan.model}, but the site ships $shipped" }
            scanner = Scanner(loadScanModel { download = it }, Projector(sealed.scan.centre, sealed.scan.seed))
        } catch (e: Throwable) {
            modelFailed = true
            onError("model: ${e.message ?: e}")
        }
    }

    val scope = rememberCoroutineScope()
    val keep: (Bundle) -> Unit = { bundle ->
        unlock?.let { u ->
            scope.launch {
                try {
                    KeptGift.put(u.keepsake(bundle))
                } catch (e: Throwable) {
                    onError("keep gift: ${e.message ?: e}")
                }
            }
        }
    }
    val keptAndOpened: (Bundle) -> Unit = { keep(it); screen = Screen.Opened(it) }
    val broken = { screen = Screen.Broken }
    val stuck = { screen = Screen.Recovery }
    when (val s = screen) {
        Screen.Waking -> Div({ classes("card") }) { P { Text("The stars are gathering…") } }
        Screen.Hunt -> Hunt(
            unlock, scanner, download, modelFailed,
            onFound = keep,
            onOpened = { screen = Screen.Opened(it) }, onBroken = broken, onStuck = stuck, onError = onError,
            onCameraProblem = { e -> screen = Screen.CameraProblem(cameraDenied(e), describeCameraError(e)) },
        )
        is Screen.CameraProblem -> CameraProblem(s, onRetry = { screen = Screen.Hunt }, onStuck = stuck)
        Screen.Recovery -> Recovery(unlock, onOpened = keptAndOpened, onBroken = broken, onError = onError, onBack = { screen = Screen.Hunt })
        is Screen.Opened -> LootBox(s.bundle, onError)
        Screen.Broken -> Message("Tangled stars", "The gift opened, but it didn't come out right. Tell him: it isn't something you did.")
        is Screen.NotReady -> Message("The stars aren't ready", "Something on this page didn't load. Try again a little later.", detail = s.detail)
    }
}

/**
 * The hunt: the camera with the sparkles over it, a Scan every [SCAN_INTERVAL_MS], each through [unlock].
 * The camera can start before the model has arrived; the sparkles stay cold until it has.
 * When a Scan opens the gift, [onFound] keeps it at once; the stars gather into the guide, then [onOpened].
 */
@Composable
private fun Hunt(
    unlock: Unlock?,
    scanner: Scanner?,
    download: Download?,
    modelFailed: Boolean,
    onFound: (Bundle) -> Unit,
    onOpened: (Bundle) -> Unit,
    onBroken: () -> Unit,
    onStuck: () -> Unit,
    onError: (String) -> Unit,
    onCameraProblem: (Throwable) -> Unit,
) {
    var video by remember { mutableStateOf<HTMLVideoElement?>(null) }
    var cameraOn by remember { mutableStateOf(false) }
    var cameraStopped by remember { mutableStateOf(false) }
    var found by remember { mutableStateOf<Bundle?>(null) }

    LaunchedEffect(found) {
        val bundle = found ?: return@LaunchedEffect
        onFound(bundle)
        delay((GATHER_SECONDS * 1000).toLong())
        onOpened(bundle)
    }

    LaunchedEffect(unlock, scanner, cameraOn) {
        val u = unlock ?: return@LaunchedEffect
        val s = scanner ?: return@LaunchedEffect
        val v = video ?: return@LaunchedEffect
        if (!cameraOn) return@LaunchedEffect
        while (isActive) {
            val start = window.performance.now()
            if (cameraEnded(v)) {
                cameraOn = false
                cameraStopped = true
                break
            }
            if (v.readyState >= 2) {
                try {
                    // The Radar warms before the candidates run, so the sparkles follow it during their KDF.
                    when (val step = u.scan(s.scan(v).projection)) {
                        is GiftOpened -> {
                            found = step.bundle
                            return@LaunchedEffect
                        }
                        GiftBroken -> return@LaunchedEffect onBroken()
                        HuntStep.Cold, HuntStep.Missed -> Unit
                    }
                } catch (e: Throwable) {
                    onError("scan: ${e.message ?: e}")
                }
            }
            delay(max(0.0, SCAN_INTERVAL_MS - (window.performance.now() - start)).toLong())
        }
    }

    Div({ classes("hunt") }) {
        CameraStage(
            cameraOn = cameraOn,
            onVideo = { video = it },
            onStarted = {
                scanner?.recent?.clear()
                unlock?.restartRadar()
                cameraStopped = false
                cameraOn = true
            },
            onError = onCameraProblem,
            startLabel = "Peer into the stars",
            overlay = { Sparkles(gathering = { found != null }) { unlock?.warmth ?: 0.0 } },
        )
        Div({ classes("caption") }) {
            P { Text(if (found != null) "The stars have gathered…" else caption(cameraOn, cameraStopped, scanner != null, modelFailed, download)) }
            if (found == null) StuckLink(onStuck)
        }
    }
}

private fun caption(cameraOn: Boolean, cameraStopped: Boolean, ready: Boolean, modelFailed: Boolean, download: Download?): String = when {
    modelFailed -> "The stars couldn't gather this time. Reload the page to try again."
    cameraStopped -> "The stars slipped away while you were gone. Tap to call them back."
    !ready -> "The stars are gathering" + (download?.let { d -> d.fraction?.let { " · ${(it * 100).roundToInt()} %" } ?: " · ${mb(d.received)}" } ?: "…")
    !cameraOn -> "Something is written in the stars, for you."
    else -> "Hold the lens to reveal what the stars have written."
}

/** How long a kept gift may take to come out of IndexedDB before the hunt starts without it. */
private const val KEPT_TIMEOUT_MS = 2_000L

private fun mb(bytes: Double) = "${(bytes / 1e6).roundToInt()} MB"

@Composable
private fun CameraProblem(problem: Screen.CameraProblem, onRetry: () -> Unit, onStuck: () -> Unit) {
    Div({ classes("card") }) {
        H1 { Text("The stars need your camera") }
        if (problem.denied) {
            P { Text("Safari isn't letting this page use the camera.") }
            P { Text("Tap aA in the address bar, then Website Settings → Camera → Allow, and try again.") }
        } else {
            P { Text("The camera didn't start. Close other apps that might be using it, and try again.") }
            P({ classes("detail") }) { Text(problem.detail) }
        }
        Button({
            classes("pill")
            onClick { onRetry() }
        }) { Text("Try again") }
        StuckLink(onStuck)
    }
}

@Composable
private fun NoLetter() {
    Message("Not yet", "This page opens with the QR code in your letter. Scan it with your camera to begin.")
}

@Composable
private fun Message(heading: String, text: String, detail: String? = null) {
    Div({ classes("card") }) {
        H1 { Text(heading) }
        P { Text(text) }
        detail?.let { P({ classes("detail") }) { Text(it) } }
    }
}

/** A button that looks like a link: a real link to `#` would replace the QR secret in the URL. */
@Composable
internal fun TextLink(label: String, onClick: () -> Unit) {
    Button({
        classes("link")
        onClick { onClick() }
    }) { Text(label) }
}

@Composable
private fun StuckLink(onStuck: () -> Unit) = TextLink("Stuck?", onStuck)
